<script setup>
// Editor de um lembrete automático de serviço. Cada lembrete tem SUA PRÓPRIA caixa
// de envio (dá pra ter lembretes por caixas diferentes). Se a caixa for WhatsApp
// oficial, o conteúdo pode ser um template aprovado (dropdown).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isTemplateCapableWhatsappInbox } from 'dashboard/helper/whatsappCampaignInbox';
import { buildReminderTemplateParameters } from './reminderTemplate';

const props = defineProps({
  reminder: { type: Object, required: true },
  inboxes: { type: Array, default: () => [] }, // caixas WhatsApp disponíveis
  templatesByInbox: { type: Object, default: () => ({}) }, // cache templates por caixa
});
const emit = defineEmits(['remove', 'loadTemplates']);
const { t } = useI18n();
const r = props.reminder;

const selectedInbox = computed(() =>
  props.inboxes.find(i => i.id === r.inbox_id)
);
const isOfficial = computed(() =>
  isTemplateCapableWhatsappInbox(selectedInbox.value)
);
const templates = computed(() => props.templatesByInbox[r.inbox_id] || []);
const selectedTemplate = computed(() =>
  templates.value.find(
    template =>
      template.name === r.wa_template_name &&
      template.language === r.wa_template_lang
  )
);
const templateParameters = ref({});
const parameterSections = computed(() =>
  ['header', 'body'].map(section => ({
    section,
    label:
      section === 'header'
        ? t('ATENDIMENTOS.services.reminders.parameterSections.header')
        : t('ATENDIMENTOS.services.reminders.parameterSections.body'),
    keys: Object.keys(templateParameters.value[section] || {}).filter(
      key => key !== 'media_type'
    ),
  }))
);
const buttonParameters = computed(() =>
  Object.entries(templateParameters.value.buttons || []).filter(
    ([, button]) => button
  )
);
const appointmentVariables = ['nome', 'servico', 'profissional', 'data', 'hora']
  .map(variable => `{{${variable}}}`)
  .join(', ');
const parameterLabel = key => {
  if (key === 'media_url') return t('ATENDIMENTOS.services.reminders.mediaUrl');
  if (key === 'media_name')
    return t('ATENDIMENTOS.services.reminders.mediaName');
  return key;
};

watch(
  selectedTemplate,
  template => {
    if (!template) return;
    try {
      templateParameters.value = buildReminderTemplateParameters(
        template,
        r.wa_template_params
      );
    } catch {
      // Um lembrete legado sem JSON precisa ter os campos preenchidos de novo.
      templateParameters.value = buildReminderTemplateParameters(template);
    }
    r.wa_template_params = JSON.stringify(templateParameters.value);
  },
  { immediate: true }
);
watch(
  templateParameters,
  parameters => {
    r.wa_template_params = JSON.stringify(parameters);
  },
  { deep: true }
);

const useTemplate = ref(!!props.reminder.wa_template_name);
const clearTemplate = () => {
  r.wa_template_name = '';
  r.wa_template_lang = '';
  r.wa_template_params = '';
  templateParameters.value = {};
};
const onInboxChange = () => {
  // Templates e variáveis pertencem à caixa que os sincronizou.
  clearTemplate();
  useTemplate.value = false;
  if (isOfficial.value) emit('loadTemplates', r.inbox_id);
};
const onMode = v => {
  useTemplate.value = v;
  if (!v) clearTemplate();
};
// valor do select = "name|lang" (templates podem repetir nome em idiomas diferentes)
const templateKey = computed({
  get: () =>
    r.wa_template_name
      ? `${r.wa_template_name}|${r.wa_template_lang || ''}`
      : '',
  set: val => {
    const [name, lang] = (val || '').split('|');
    r.wa_template_params = '';
    r.wa_template_name = name || '';
    r.wa_template_lang = lang || '';
  },
});
</script>

<template>
  <div class="p-3 border rounded-lg border-n-weak bg-n-alpha-1">
    <div class="flex items-center gap-2 mb-2">
      <input
        v-model="r.label"
        class="flex-1 w-full px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :placeholder="t('ATENDIMENTOS.services.reminders.labelPlaceholder')"
      />
      <label class="flex items-center gap-1 text-xs"
        ><input v-model="r.is_active" type="checkbox" />
        {{ t('ATENDIMENTOS.services.reminders.active') }}</label
      >
      <button
        class="text-xs text-n-ruby-11 hover:underline"
        @click="emit('remove')"
      >
        {{ t('ATENDIMENTOS.services.reminders.removeShort') }}
      </button>
    </div>

    <!-- timing -->
    <div class="flex flex-wrap items-center gap-1 mb-2 text-xs text-n-slate-11">
      <span>{{ t('ATENDIMENTOS.services.reminders.send') }}</span>
      <input
        v-model.number="r.days_before"
        type="number"
        min="0"
        class="reset-base px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-[52px]"
      /><span>{{ t('ATENDIMENTOS.services.reminders.days') }}</span>
      <input
        v-model.number="r.hours_before"
        type="number"
        min="0"
        class="reset-base px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-[52px]"
      /><span>{{ t('ATENDIMENTOS.services.reminders.hours') }}</span>
      <input
        v-model.number="r.minutes_before"
        type="number"
        min="0"
        class="reset-base px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-[52px]"
      /><span>{{ t('ATENDIMENTOS.services.reminders.minutes') }}</span>
      <div class="relative flex-1 min-w-[140px]">
        <select
          v-model="r.timing"
          class="w-full appearance-none !bg-none pl-2 pr-8 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="before">
            {{ t('ATENDIMENTOS.services.reminders.beforeConsult') }}
          </option>
          <option value="after">
            {{ t('ATENDIMENTOS.services.reminders.afterConsult') }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-10"
        />
      </div>
    </div>

    <!-- caixa de envio (por lembrete) -->
    <div class="relative mb-2">
      <select
        v-model="r.inbox_id"
        data-testid="reminder-inbox"
        class="w-full appearance-none !bg-none pl-2 pr-8 py-2 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        @change="onInboxChange"
      >
        <option :value="null">
          {{ t('ATENDIMENTOS.services.reminders.selectInbox') }}
        </option>
        <option v-for="i in inboxes" :key="i.id" :value="i.id">
          {{ i.name
          }}{{
            isTemplateCapableWhatsappInbox(i)
              ? ` ${t('ATENDIMENTOS.services.reminders.officialAPI')}`
              : ''
          }}
        </option>
      </select>
      <span
        class="i-lucide-chevron-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-10"
      />
    </div>

    <!-- modo: mensagem livre / template (template só em caixa oficial) -->
    <div class="flex gap-1 mb-2">
      <button
        class="px-3 py-1 text-xs rounded-md text-n-slate-10"
        :class="!useTemplate ? 'bg-n-background text-n-slate-12 shadow-sm' : ''"
        @click="onMode(false)"
      >
        {{ t('ATENDIMENTOS.services.reminders.freeMessage') }}
      </button>
      <button
        v-if="isOfficial"
        class="px-3 py-1 text-xs rounded-md text-n-slate-10"
        :class="useTemplate ? 'bg-n-background text-n-slate-12 shadow-sm' : ''"
        @click="onMode(true)"
      >
        {{ t('ATENDIMENTOS.services.reminders.waTemplate') }}
      </button>
    </div>

    <template v-if="!useTemplate || !isOfficial">
      <textarea
        v-model="r.message"
        rows="2"
        class="w-full px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :placeholder="t('ATENDIMENTOS.services.reminders.messagePlaceholder')"
      />
    </template>
    <template v-else>
      <div v-if="!templates.length" class="text-[0.6875rem] text-n-amber-11">
        {{ t('ATENDIMENTOS.services.reminders.noApprovedTemplates') }}
      </div>
      <div v-else class="relative">
        <select
          v-model="templateKey"
          data-testid="reminder-template"
          class="w-full appearance-none !bg-none pl-2 pr-8 py-2 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="">
            {{ t('ATENDIMENTOS.services.reminders.selectTemplate') }}
          </option>
          <option
            v-for="tpl in templates"
            :key="`${tpl.name}|${tpl.language}`"
            :value="`${tpl.name}|${tpl.language}`"
          >
            {{ tpl.name }} ({{ tpl.language }})
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-10"
        />
      </div>
      <div v-if="selectedTemplate" class="mt-3 space-y-3">
        <p class="text-xs text-n-slate-11">
          {{
            t('ATENDIMENTOS.services.reminders.parameterHint', {
              variables: appointmentVariables,
            })
          }}
        </p>
        <div
          v-for="{ section, label, keys } in parameterSections"
          :key="section"
        >
          <label
            v-for="key in keys"
            :key="`${section}-${key}`"
            class="flex flex-col gap-1 mb-2 text-xs"
          >
            <span>
              {{ label }}
              {{ parameterLabel(key) }}
            </span>
            <input
              v-model="templateParameters[section][key]"
              :data-testid="`template-${section}-${key}`"
              class="px-2 py-1 rounded-lg border border-n-weak bg-n-background text-n-slate-12"
            />
          </label>
        </div>
        <label
          v-for="[index, button] in buttonParameters"
          :key="`button-${index}`"
          class="flex flex-col gap-1 mb-2 text-xs"
        >
          <span>
            {{ t('ATENDIMENTOS.services.reminders.parameterSections.button') }}
            {{ Number(index) + 1 }}
          </span>
          <input
            v-model="button.parameter"
            :data-testid="`template-button-${index}`"
            class="px-2 py-1 rounded-lg border border-n-weak bg-n-background text-n-slate-12"
          />
        </label>
      </div>
    </template>
  </div>
</template>
